import { ICountryUsersCountDTO, IDelayedPanicRequestDTO, IPanicRequestDTO, ITrackingHourlyDTO, IUserLocationDTO, IUserLocationRequestDTO, MobileAppDashboardClient } from 'api/swagger/ApiClient';
import { autoinject } from 'aurelia-framework';
import { tryExecute } from 'framework/error_handling/error-utils';
import { ActiveUsersData, DelayedPanicRequestModel, EventType, InstalledVersionsData, MobileAppEvent, PanicRequestsData, TrackingUsersData, UserLocationRequestModel } from 'model/mobile-app-dashboard';
import * as E from 'support/errors';
import * as moment from 'moment';

@autoinject
export class MobileAppDashboardService {
  activeUsers: ActiveUsersData;
  installedVersions: InstalledVersionsData;
  trackingUsers: TrackingUsersData;
  trackingHourly: ITrackingHourlyDTO[];
  panicRequests: PanicRequestsData;
  delayedPanicRequests: DelayedPanicRequestModel[] = [];
  usersLatestLocations: IUserLocationDTO[];
  countryUsersCounts: ICountryUsersCountDTO[];
  userLocationRequests: UserLocationRequestModel[] = [];
  mobileAppEvents: MobileAppEvent[] = [];
  refreshTrackingTimer: NodeJS.Timeout = null;
  refreshPanicRequestsTimer: NodeJS.Timeout = null;
  showUserLocation: (userUniqueId: string, userName: string) => Promise<void> = null;
  showPanicRequest: (panicRequest: IPanicRequestDTO) => Promise<void> = null;
  recalculateDelayedPanicRequestsTimer: NodeJS.Timeout = null;

  constructor(private api: MobileAppDashboardClient) {
  }

  initializeService(showUserLocation: (userUniqueId: string, userName: string) => Promise<void>, showPanicRequest: (panicRequest: IPanicRequestDTO) => Promise<void>) {
    this.showUserLocation = showUserLocation;
    this.showPanicRequest = showPanicRequest;
    this.getActiveUsers().then((activeUsers) => { this.activeUsers = activeUsers });
    this.getInstalledVersions().then((installedVersions) => { this.installedVersions = installedVersions });
    this.reloadTracking();
    this.reloadPanicRequests();
    this.refreshTrackingTimer = setInterval(() => this.refreshTracking(), 20 * 1000);
    this.refreshPanicRequestsTimer = setInterval(() => this.refreshPanicRequests(), 15 * 1000);
    this.recalculateDelayedPanicRequestsTimer = setInterval(() => {
      this.delayedPanicRequests.forEach(x => {
        x.activeIn = this.calculateDelayedPanicRequestActiveIn(x);
      })
    }, 1000);
  }

  public stopService() {
    clearInterval(this.refreshTrackingTimer);
    clearInterval(this.refreshPanicRequestsTimer);
    clearInterval(this.recalculateDelayedPanicRequestsTimer);
  }

  public async requestUserLocation(userUniqueId: string, userName: string): Promise<UserLocationRequestModel> {
    const locationRequest = this.mapUserLocationRequest(await this.api.requestUserLocation(userUniqueId));
    if (locationRequest.userName == null)
      locationRequest.userName = userName;
    this.userLocationRequests.push(locationRequest);
    this.reloadMobileAppEvents();
    return locationRequest;
  }

  private refreshTracking() {
    return tryExecute(() => this.reloadTracking(), E.SERVICE_TEMPORARILY_UNAVAILABLE_MESSAGE, E.SERVICE_TEMPORARILY_UNAVAILABLE_TITLE, true);
  }

  private refreshPanicRequests() {
    return tryExecute(() => this.reloadPanicRequests(), E.SERVICE_TEMPORARILY_UNAVAILABLE_MESSAGE, E.SERVICE_TEMPORARILY_UNAVAILABLE_TITLE, true);
  }

  private async reloadTracking() {
    await Promise.all(
      [this.loadTrackingUsers(), this.loadTrackingHourly(), this.loadUsersLatestLocations()]
    );
    await this.reloadUserLocationRequests();
  }

  private async reloadPanicRequests() {
    this.panicRequests = await this.getPanicRequests();
    this.delayedPanicRequests = (await this.api.getDelayedPanicRequests()).map(x => this.mapDelayedPanicRequest(x))
      .sort((a, b) => {
        if (a.state.value === b.state.value) {
          if (a.state.value === 100)
            return moment.utc(a.delayedUntil).diff(moment.utc(b.delayedUntil));
          else
            return moment.utc(b.stateUpdatedOn).diff(moment.utc(a.stateUpdatedOn));
        }
        else {
          return (a.state.value === 300 ? 1 : a.state.value) - (b.state.value === 300 ? 1 : b.state.value);
        }
      });
  }

  private async reloadUserLocationRequests() {
    this.userLocationRequests = (await this.api.getUserLocationRequests()).map(x => this.mapUserLocationRequest(x));
    this.reloadMobileAppEvents();
  }

  private reloadMobileAppEvents() {
    this.mobileAppEvents = this.userLocationRequests.map(x => { return { type: EventType.UserLocationRequest, date: x.requestedOn, data: x } as MobileAppEvent }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  private async getActiveUsers(): Promise<ActiveUsersData> {
    const activeUsers = await this.api.getActiveUsers();
    return {
      headerActiveUsers: activeUsers.find(x => x.dateRangeName === 'Last month'),
      activeUsers: activeUsers
    } as ActiveUsersData;
  }

  private async getInstalledVersions(): Promise<InstalledVersionsData> {
    const installedVersions = await this.api.getInstalledVersions();
    return {
      latest: installedVersions.length > 0 ? installedVersions[0].count : 0,
      total: installedVersions.map(x => x.count).reduce((previous, current) => previous + current, 0),
      installedVersions: installedVersions
    } as InstalledVersionsData;
  }

  private async loadTrackingUsers(): Promise<void> {
    const trackingUsers = await this.api.getTrackingUsers();
    this.trackingUsers = {
      headerTrackingUsers: trackingUsers.find(x => x.dateRangeName === 'Last day'),
      trackingUsers: trackingUsers
    } as TrackingUsersData;
  }

  private async loadTrackingHourly(): Promise<void> {
    this.trackingHourly = await this.api.getTrackingHourly();
  }

  private async loadUsersLatestLocations(): Promise<void> {
    const usersLatestLocations = await this.api.getUsersLatestsLocations();
    this.usersLatestLocations = usersLatestLocations.usersLatestLocations;
    this.countryUsersCounts = usersLatestLocations.countryUsersCounts;
  }

  private async getPanicRequests(): Promise<PanicRequestsData> {
    const panicRequests = await this.api.getPanicRequests();
    const activePanicRequests = panicRequests.filter(x => x.isActive);
    return {
      active: activePanicRequests.length,
      isTest: !activePanicRequests.some(x => !x.isTest),
      panicRequests: panicRequests
    } as PanicRequestsData;
  }

  private mapUserLocationRequest(dto: IUserLocationRequestDTO): UserLocationRequestModel {
    return {
      ...dto,
      hasLocation: this.usersLatestLocations.some(x => x.user.uniqueId === dto.userUniqueId),
      showLocation: this.showUserLocation
    } as UserLocationRequestModel;
  }

  private mapDelayedPanicRequest(dto: IDelayedPanicRequestDTO): DelayedPanicRequestModel {
    return {
      ...dto,
      activeIn: this.calculateDelayedPanicRequestActiveIn(dto),
      panicRequest: dto.panicRequestId ? this.panicRequests.panicRequests.find(x => x.id === dto.panicRequestId) : null,
      showPanicRequest: this.showPanicRequest
    } as DelayedPanicRequestModel;
  }

  private calculateDelayedPanicRequestActiveIn(dto: IDelayedPanicRequestDTO) {
    const delayedUntil = moment.utc(dto.delayedUntil);
    const now = moment.utc();
    return delayedUntil > now ? moment.utc(delayedUntil.diff(now)).format('HH:mm:ss') : `-${moment.utc(now.diff(delayedUntil)).format('HH:mm:ss')}`;
  }
}
